/**
 * Shared axios resilience interceptors.
 *
 * Provides two response interceptors that can be attached to any axios instance:
 * 1. Retry — replays transient failures (502/503/504, network errors) on safe methods
 * 2. Token refresh — handles 401 by refreshing the access token and replaying
 *
 * One implementation, three consumers: global axios, chatApi, copilotApi.
 */
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { TokenRefresh } from '../models/token.model';
import { authApiKey, authRefresh, authRefreshToken, authClear, authToken } from './authentication.script';

// ---------------------------------------------------------------------------
// Retry
// ---------------------------------------------------------------------------

const RETRY_MAX = 2
// `options` is present for documentation: a preflight rejected by the browser
// never surfaces as an OPTIONS request in the interceptor — the failure appears
// as a network error on the actual request (GET, POST…), and replaying that
// request re-triggers the preflight automatically.
const RETRY_SAFE_METHODS = ['get', 'head', 'options']
const RETRY_STATUS_CODES = [502, 503, 504]

interface RetryableConfig extends AxiosRequestConfig {
  _retry?: boolean;
  _retryCount?: number;
}

function isRefreshRequest(url: string | undefined): boolean {
  return !!url && url.includes('/auth/refresh')
}

function isRetryable(error: any): boolean {
  const config = error.config as RetryableConfig | undefined
  if (!config) return false

  const method = (config.method || '').toLowerCase()
  if (!RETRY_SAFE_METHODS.includes(method)) return false

  if (isRefreshRequest(config.url)) return false

  if ((config._retryCount ?? 0) >= RETRY_MAX) return false

  if (error.response && RETRY_STATUS_CODES.includes(error.response.status)) return true

  if (!error.response && error.code !== 'ERR_CANCELED') return true

  return false
}

function retryDelay(attempt: number): Promise<void> {
  const base = 300 * Math.pow(3, attempt - 1)
  const jitter = base * 0.3 * Math.random()
  return new Promise(resolve => setTimeout(resolve, base + jitter))
}

/**
 * Attach the transient-error retry interceptor to an axios instance.
 * Safe methods only (GET/HEAD/OPTIONS), 2 retries, exponential backoff with jitter.
 */
export function attachRetryInterceptor(instance: AxiosInstance): number {
  return instance.interceptors.response.use(
    response => response,
    async (error) => {
      if (isRetryable(error)) {
        const config = error.config as RetryableConfig
        config._retryCount = (config._retryCount ?? 0) + 1
        await retryDelay(config._retryCount)
        return instance(config)
      }
      return Promise.reject(error)
    }
  )
}

// ---------------------------------------------------------------------------
// Token refresh (401 → refresh → replay)
// ---------------------------------------------------------------------------

// Shared across all instances — one refresh at a time, all queued requests
// get the new token once it's ready.
let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (error: any) => void }> = []

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) { prom.resolve(token) }
    else { prom.reject(error) }
  })
  failedQueue = []
}

async function performRefresh(): Promise<TokenRefresh | null> {
  const refreshToken = authRefresh()
  if (!refreshToken) return null

  const apiKey = authApiKey()
  const url = `${process.env.REACT_APP_PORT_AUTH}/api/v1/${apiKey}/auth/refresh`

  const response = await axios.create().post(url, { refresh_token: refreshToken })
  if (response.status === 200 && response.data?.success !== false) {
    const data = response.data?.data ?? response.data
    return data as TokenRefresh
  }
  return null
}

function forceLogout() {
  authClear()
  window.location.href = '/auth/login'
}

/**
 * Attach the 401 token-refresh interceptor to an axios instance.
 * On 401: refresh the access token via Digital Auth, update the cookie,
 * replay the original request with the new token, queue concurrent requests.
 */
export function attachRefreshInterceptor(instance: AxiosInstance): number {
  return instance.interceptors.response.use(
    response => response,
    async (error) => {
      const originalRequest = error.config as RetryableConfig

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshRequest(originalRequest.url)) {
          processQueue(error, null)
          forceLogout()
          return Promise.reject(error)
        }

        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          }).then((token) => {
            originalRequest.headers = { ...originalRequest.headers, Authorization: token }
            return instance(originalRequest)
          }).catch((err) => {
            return Promise.reject(err)
          })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          const tokenData = await performRefresh()
          if (tokenData) {
            authRefreshToken(tokenData)
            const newToken = authToken()
            processQueue(null, newToken)
            originalRequest.headers = { ...originalRequest.headers, Authorization: newToken }
            return instance(originalRequest)
          } else {
            processQueue(error, null)
            forceLogout()
            return Promise.reject(error)
          }
        } catch (err) {
          processQueue(err, null)
          forceLogout()
          return Promise.reject(err)
        } finally {
          isRefreshing = false
        }
      }

      return Promise.reject(error)
    }
  )
}

export { isRefreshing }
